/**
 * Pure helper — suggest an intensity level for a SpraySession based on the
 * round's planned water/ha vs. the selected machine's latest calibration's
 * per-level water/ha. Used by SpraySessionForm.
 *
 * Algorithm:
 *  1. waterPerHectare missing / non-positive → null label, rationale 'no_input'.
 *  2. intensityLevels missing / empty → null label, rationale 'no_levels'.
 *  3. Filter to levels with positive finite litresPerHectare. If none survive →
 *     null label, rationale 'no_litres_per_hectare'.
 *  4. Pick the level with min(|level.litresPerHectare - waterPerHectare|).
 *     Tiebreaker: lower intensity wins ('low' < 'medium' < 'medium/heavy' < 'heavy').
 *
 * Pure, no I/O. See
 * docs/superpowers/archive/specs/2026-05-17-spray-session-intensity-readd-design.md.
 */

export type IntensityLabel = 'low' | 'medium' | 'medium/heavy' | 'heavy';

export interface IntensityLevel {
  label: IntensityLabel;
  litresPerHectare?: number;
}

export type SuggestionRationale =
  | 'closest_match'
  | 'no_input'
  | 'no_levels'
  | 'no_litres_per_hectare';

export interface SuggestionResult {
  label: IntensityLabel | null;
  rationale: SuggestionRationale;
  delta?: number;
  matchedLitresPerHectare?: number;
}

const INTENSITY_ORDER: readonly IntensityLabel[] = ['low', 'medium', 'medium/heavy', 'heavy'];

function intensityRank(label: IntensityLabel): number {
  return INTENSITY_ORDER.indexOf(label);
}

export function suggestIntensityLevel(
  waterPerHectare: number | null | undefined,
  intensityLevels: IntensityLevel[] | null | undefined,
): SuggestionResult {
  if (waterPerHectare == null || !Number.isFinite(waterPerHectare) || waterPerHectare <= 0) {
    return { label: null, rationale: 'no_input' };
  }
  if (!intensityLevels || intensityLevels.length === 0) {
    return { label: null, rationale: 'no_levels' };
  }

  const eligible = intensityLevels.filter(
    l => l.litresPerHectare != null
      && Number.isFinite(l.litresPerHectare)
      && (l.litresPerHectare as number) > 0,
  );
  if (eligible.length === 0) {
    return { label: null, rationale: 'no_litres_per_hectare' };
  }

  let best = eligible[0];
  let bestDelta = Math.abs((best.litresPerHectare as number) - waterPerHectare);
  for (let i = 1; i < eligible.length; i++) {
    const candidate = eligible[i];
    const candidateDelta = Math.abs((candidate.litresPerHectare as number) - waterPerHectare);
    if (
      candidateDelta < bestDelta
      || (candidateDelta === bestDelta && intensityRank(candidate.label) < intensityRank(best.label))
    ) {
      best = candidate;
      bestDelta = candidateDelta;
    }
  }

  return {
    label: best.label,
    rationale: 'closest_match',
    delta: bestDelta,
    matchedLitresPerHectare: best.litresPerHectare as number,
  };
}
